import StatusBadge from "@/components/get-status-badge";
import Icons from "@/components/Icon";
import { menuItems } from "@/config/menu";
import cn from "@/utils/class-names";
import { canAccess } from "@/utils/Utils";
import { useEffect } from "react";
import { PiCaretDownBold } from "react-icons/pi";
import { Link, useLocation } from "react-router-dom";
import { Collapse, Title } from "rizzui";

export function SidebarMenu() {
    const location = useLocation();

    const locationName = location.pathname.replace("/", "");

    useEffect(() => {
        // Set the document title
        if (locationName === "") document.title = import.meta.env.VITE_APP_NAME;
        else
            document.title = `${import.meta.env.VITE_APP_NAME}  | ${locationName}`;
    }, [location]);

    return (
        <div className="mt-4 pb-3 3xl:mt-6">
            {menuItems.filter(item => !item.isHidden).map((item, index) => {
                const isActive = location.pathname === (item.href as string);
                const pathnameExistInDropdowns: any =
                    item.dropdownItems?.filter(
                        (dropdownItem) =>
                            dropdownItem.href === location.pathname,
                    );

                const isDropdownOpen = Boolean(
                    pathnameExistInDropdowns?.length,
                );

                if (!canAccess({ role: item.role, permission: item.permission })) {
                    return <></>;
                }

                if (!item.href) {
                    return <Title key={item.name + "-" + index} as="h6"
                        className={cn(
                            "mb-2 truncate px-6 text-xs font-normal uppercase tracking-widest text-gray-500 2xl:px-8",
                            index !== 0 && "mt-6 3xl:mt-7",
                        )}>
                        {item.name}
                    </Title>
                }

                if (item.dropdownItems && item.dropdownItems.length > 0) {
                    return <Collapse key={item.name + "-" + index}
                        defaultOpen={isDropdownOpen}
                        header={({ open, toggle }) => (
                            <div
                                onClick={toggle}
                                className={cn(
                                    "group relative mx-3 flex cursor-pointer items-center justify-between rounded-md px-3 py-2 font-medium lg:my-1 2xl:mx-5 2xl:my-2",
                                    isDropdownOpen
                                        ? "before:top-2/5 text-primary before:absolute before:-start-3 before:block before:h-4/5 before:w-1 before:rounded-ee-md before:rounded-se-md before:bg-primary 2xl:before:-start-5"
                                        : "text-gray-700 transition-colors duration-200 hover:bg-gray-100 dark:text-gray-700/90 dark:hover:text-gray-700",
                                )}
                            >
                                {item.icon && (
                                    <span className="flex items-center">
                                        <span
                                            className={cn(
                                                "me-2 inline-flex h-5 w-5 items-center justify-center rounded-md [&>svg]:h-[20px] [&>svg]:w-[20px]",
                                                isDropdownOpen
                                                    ? "text-primary"
                                                    : "text-gray-800 dark:text-gray-500 dark:group-hover:text-gray-700",
                                            )}
                                        >
                                            {typeof item.icon ===
                                                "string" ? (
                                                <Icons
                                                    icon={
                                                        item.icon
                                                    }
                                                />
                                            ) : (
                                                item.icon
                                            )}
                                        </span>

                                        {item.name}
                                    </span>
                                )}

                                <PiCaretDownBold
                                    strokeWidth={3}
                                    className={cn(
                                        "h-3.5 w-3.5 -rotate-90 text-gray-500 transition-transform duration-200 rtl:rotate-90",
                                        open &&
                                        "rotate-0 rtl:rotate-0",
                                    )}
                                />
                            </div>
                        )}
                    >
                        {item.dropdownItems?.map(
                            (dropdownItem, index) => {
                                const isChildActive =
                                    location.pathname ===
                                    (dropdownItem.href as string);

                                return (
                                    canAccess({
                                        role: dropdownItem.role,
                                        permission:
                                            dropdownItem.permission,
                                    }) && (
                                        <Link
                                            to={
                                                dropdownItem.href!
                                            }
                                            key={
                                                dropdownItem.name +
                                                index
                                            }
                                            className={cn(
                                                "mx-3.5 mb-0.5 flex items-center justify-between rounded-md px-3.5 py-2 font-medium capitalize last-of-type:mb-1 lg:last-of-type:mb-2 2xl:mx-5",
                                                isChildActive
                                                    ? "text-primary"
                                                    : "text-gray-500 transition-colors duration-200 hover:bg-gray-100 hover:text-gray-900",
                                            )}
                                        >
                                            <div className="flex items-center truncate">
                                                <span
                                                    className={cn(
                                                        "me-[18px] ms-1 inline-flex h-1 w-1 rounded-full bg-current transition-all duration-200",
                                                        isChildActive
                                                            ? "bg-primary ring-[1px] ring-primary"
                                                            : "opacity-40",
                                                    )}
                                                />{" "}
                                                <span className="truncate">
                                                    {
                                                        dropdownItem.name
                                                    }
                                                </span>
                                            </div>
                                            {dropdownItem.badge?.length ? (
                                                <StatusBadge
                                                    status={
                                                        dropdownItem.badge
                                                    }
                                                />
                                            ) : null}
                                        </Link>
                                    )
                                );
                            },
                        )}
                    </Collapse>;
                }

                return (
                    <Link key={item.name + "-" + index} to={item.href}
                        className={cn(
                            "group relative mx-3 my-0.5 flex items-center justify-between rounded-md px-3 py-2 font-medium capitalize lg:my-1 2xl:mx-5 2xl:my-2",
                            isActive
                                ? "before:top-2/5 text-primary before:absolute before:-start-3 before:block before:h-4/5 before:w-1 before:rounded-ee-md before:rounded-se-md before:bg-primary 2xl:before:-start-5"
                                : "text-gray-700 transition-colors duration-200 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-700/90",
                        )}
                    >
                        <div className="flex items-center truncate">
                            <span
                                className={cn(
                                    "me-2 inline-flex size-5 items-center justify-center rounded-md [&>svg]:size-5",
                                    isActive
                                        ? "text-primary"
                                        : "text-gray-800 dark:text-gray-500 dark:group-hover:text-gray-700",
                                )}
                            >
                                {typeof item.icon ===
                                    "string" ? (
                                    <Icons
                                        icon={item.icon}
                                    />
                                ) : (
                                    item.icon
                                )}
                            </span>

                            <span className="truncate">
                                {item.name}
                            </span>
                        </div>
                        {item.badge?.length ? (
                            <StatusBadge
                                status={item.badge}
                            />
                        ) : null}
                    </Link>
                );
            })}
        </div>
    );
}